'use client';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import useScrollReveal from '@/hooks/useScrollReveal';

interface AudienceCard {
  title: string;
  description: string;
  icon: string;
  accentColor: string;
  image: string;
  imageAlt: string;
}

export default function WhoWeServe() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  const audiences: AudienceCard[] = [
  {
    title: 'Second-Chance & Justice-Impacted Entrepreneurs',
    description: 'You\'ve got skills that took years to develop. But without a professional brand and digital presence, you\'re invisible to the clients who can pay what you\'re worth. We build the credibility infrastructure that lets your work speak for itself.',
    icon: 'HandRaisedIcon',
    accentColor: 'brand-teal',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12b94a2f7-1784915378529.png",
    imageAlt: 'Determined entrepreneur working on building their second-chance business in urban workspace'
  },
  {
    title: 'Nonprofits & Social Impact Organizations',
    description: 'You\'re doing transformative work with a website that looks like 2005 and donor management held together with spreadsheets. You need infrastructure that matches your mission—systems that scale, branding that builds trust, automation that frees your team to focus on impact.',
    icon: 'HeartIcon',
    accentColor: 'brand-orange',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca063b61-1767033344850.png",
    imageAlt: 'Nonprofit team collaborating on community impact project in creative workspace'
  },
  {
    title: 'Mission-Driven Founder-Led Businesses',
    description: 'You didn\'t start this business to be mediocre. You want to build something excellent, something that reflects your values. We work with founders who refuse to compromise on quality and are ready to invest in the infrastructure that supports that standard.',
    icon: 'SparklesIcon',
    accentColor: 'brand-green',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce8b96ce-1767864527453.png",
    imageAlt: 'Mission-driven founder leading team meeting with intentional focus and edge'
  }];


  return (
    <section
      id="who-we-serve"
      ref={sectionRef}
      className={`py-12 sm:py-16 md:py-24 lg:py-32 bg-brand-offwhite transition-all duration-1000 ${
      sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`
      }>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-brand-plum mb-3 sm:mb-4 font-bold leading-tight">
            Who This Is For
          </h2>
          <p className="font-body text-sm sm:text-base md:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
            We're selective about who we work with. Here's who we serve best.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {audiences.map((audience, index) => {
            const { ref: cardRef, isVisible: cardVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={cardRef}
                className={`group relative h-[400px] sm:h-[450px] md:h-[500px] overflow-hidden transition-all duration-700 hover:shadow-2xl ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
            
              {/* Background image */}
              <AppImage
                  src={audience.image}
                  alt={audience.imageAlt}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
            
              
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/65 group-hover:bg-black/55 transition-colors duration-500"></div>
              
              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-${audience.accentColor} flex items-center justify-center mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={audience.icon} size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <h3 className="font-headline text-lg sm:text-xl text-white mb-2 sm:mb-3 font-bold leading-tight">
                  {audience.title}
                </h3>
                <p className="font-body text-xs sm:text-sm text-white/90 leading-relaxed">
                  {audience.description}
                </p>
              </div>
              
              {/* Accent bar */}
              <div className={`absolute top-0 left-0 w-1 h-full bg-${audience.accentColor} group-hover:w-2 transition-all duration-300`}></div>
            </div>);

          })}
        </div>
      </div>
    </section>);

}